'use client';
import React, { useState } from 'react';
import { FiTrash2, FiInfo } from 'react-icons/fi';

import {
  Box,
  Button,
  Flex,
  Text,
  Icon,
  Alert,
  // HStack,
  Input,
  Stack,
  // Badge,
  Checkbox,
  List,
  ListItem,
} from '../../index';
import { Dialog } from '../modal';
import { LoadingState } from '../loading-state';

export interface DeleteConfirmationDialogProps {
  // State
  isOpen: boolean;
  onClose: () => void;

  // Resource info
  resourceName: string;
  resourceNamePlural?: string;
  itemName?: string;
  itemNames?: string[];

  // Delete configuration
  onDelete: () => void | Promise<void>;
  isDeleting?: boolean;

  // Confirmation
  requireConfirmation?: boolean;
  confirmationText?: string;
  confirmationPlaceholder?: string;

  // Warnings
  warnings?: string[];
  cascadeInfo?: {
    resource: string;
    count: number;
  }[];

  // Soft delete
  isSoftDelete?: boolean;
  canRestore?: boolean;
  restoreInfo?: string;

  // Bulk delete
  isBulkDelete?: boolean;
  selectedCount?: number;
  totalCount?: number;

  // Error handling
  error?: Error | string | null;
  onRetry?: () => void;

  // Customization
  title?: string;
  description?: string;
  deleteButtonText?: string;
  cancelButtonText?: string;
  deleteButtonColorScheme?: string;
  showIcon?: boolean;
}

export function DeleteConfirmationDialog({
  // State
  isOpen,
  onClose,

  // Resource info
  resourceName,
  resourceNamePlural,
  itemName,
  itemNames = [],

  // Delete configuration
  onDelete,
  isDeleting = false,

  // Confirmation
  requireConfirmation = false,
  confirmationText,
  confirmationPlaceholder,

  // Warnings
  warnings = [],
  cascadeInfo = [],

  // Soft delete
  isSoftDelete = false,
  canRestore = false,
  restoreInfo,

  // Bulk delete
  isBulkDelete = false,
  selectedCount = 0,
  // totalCount = 0,

  // Error handling
  error = null,
  onRetry,

  // Customization
  title,
  description,
  deleteButtonText,
  cancelButtonText = 'Cancel',
  deleteButtonColorScheme = 'red',
  showIcon = true,
}: DeleteConfirmationDialogProps) {
  const [confirmationInput, setConfirmationInput] = useState('');
  const [acknowledgedWarnings, setAcknowledgedWarnings] = useState(false);

  const pluralName = resourceNamePlural || `${resourceName}s`;
  const hasWarnings = warnings.length > 0 || cascadeInfo.length > 0;
  // const requiresAcknowledgment = hasWarnings && !acknowledgedWarnings;

  // Generate default title and description
  const defaultTitle = isBulkDelete
    ? `Delete ${selectedCount} ${selectedCount === 1 ? resourceName : pluralName}`
    : `Delete ${resourceName}`;

  const defaultDescription = isBulkDelete
    ? `Are you sure you want to delete ${selectedCount} ${selectedCount === 1 ? resourceName.toLowerCase() : pluralName.toLowerCase()}?`
    : itemName
      ? `Are you sure you want to delete "${itemName}"?`
      : `Are you sure you want to delete this ${resourceName.toLowerCase()}?`;

  const finalTitle = title || defaultTitle;
  const finalDescription = description || defaultDescription;

  // Check if confirmation is valid
  const isConfirmationValid =
    !requireConfirmation || (confirmationText && confirmationInput === confirmationText);

  // Check if delete is allowed
  const canDelete = isConfirmationValid && (!hasWarnings || acknowledgedWarnings) && !isDeleting;

  const handleDelete = async () => {
    if (!canDelete) return;

    try {
      await onDelete();
      onClose();
    } catch {
      // Error will be displayed in the dialog
    }
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(e: { open: boolean }) => !e.open && onClose()}
      size="md"
      closeOnInteractOutside={!isDeleting}
      closeOnEscape={!isDeleting}
    >
      <Dialog.Backdrop />
      <Dialog.Content {...({} as Record<string, unknown> & { children?: React.ReactNode })}>
        <Dialog.Header>
          <Flex alignItems="center" gap="2">
            {showIcon && (
              <Icon
                as={FiTrash2}
                color={deleteButtonColorScheme + '.500'}
                width="20px"
                height="20px"
              >
                <FiTrash2 />
              </Icon>
            )}
            {finalTitle}
          </Flex>
          {!isDeleting && <Dialog.CloseTrigger />}
        </Dialog.Header>
        <Dialog.Body>
          <Stack gap="16px">
            {/* Error state */}
            {error && (
              <Alert.Root status="error">
                <Alert.Indicator />
                <Box>
                  <Alert.Title>Delete failed</Alert.Title>
                  <Alert.Description>
                    {typeof error === 'string' ? error : error.message}
                  </Alert.Description>
                </Box>
              </Alert.Root>
            )}

            {/* Loading state */}
            {isDeleting && !error && (
              <LoadingState text={`Deleting ${isBulkDelete ? pluralName : resourceName}...`} />
            )}

            {/* Main content */}
            {!isDeleting && !error && (
              <>
                {/* Description */}
                <Text>{finalDescription}</Text>

                {/* Soft delete info */}
                {isSoftDelete && (
                  <Alert.Root status="info">
                    <Alert.Indicator />
                    <Box>
                      <Alert.Title fontSize="sm">This is a soft delete</Alert.Title>
                      <Alert.Description fontSize="sm">
                        {canRestore
                          ? restoreInfo || 'This item can be restored from the trash.'
                          : 'This item will be marked as deleted but not permanently removed.'}
                      </Alert.Description>
                    </Box>
                  </Alert.Root>
                )}

                {/* Bulk delete info */}
                {isBulkDelete && itemNames.length > 0 && (
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" marginBottom="8px">
                      Items to be deleted:
                    </Text>
                    <Box
                      maxHeight="200px"
                      overflowY="auto"
                      border="1px solid"
                      borderRadius="md"
                      padding="8px"
                    >
                      <List spacing={1}>
                        {itemNames.map((name, index) => (
                          <ListItem key={index} fontSize="sm">
                            • {name}
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Box>
                )}

                {/* Warnings */}
                {warnings.length > 0 && (
                  <Alert.Root status="warning">
                    <Alert.Indicator />
                    <Box>
                      <Alert.Title fontSize="sm">Warning</Alert.Title>
                      <Stack gap="4px" marginTop="4px">
                        {warnings.map((warning, index) => (
                          <Text key={index} fontSize="sm">
                            {warning}
                          </Text>
                        ))}
                      </Stack>
                    </Box>
                  </Alert.Root>
                )}

                {/* Cascade delete info */}
                {cascadeInfo.length > 0 && (
                  <Alert.Root status="warning">
                    <Alert.Indicator />
                    <Box>
                      <Alert.Title fontSize="sm">Related items will also be deleted</Alert.Title>
                      <Stack gap="4px" marginTop="4px">
                        {cascadeInfo.map((info, index) => (
                          <Text key={index} fontSize="sm">
                            • {info.count} {info.resource}
                          </Text>
                        ))}
                      </Stack>
                    </Box>
                  </Alert.Root>
                )}

                {/* Warning acknowledgment */}
                {hasWarnings && (
                  <Checkbox
                    checked={acknowledgedWarnings}
                    onCheckedChange={(checked) => setAcknowledgedWarnings(!!checked)}
                  >
                    <Text fontSize="sm">I understand the consequences of this action</Text>
                  </Checkbox>
                )}

                {/* Confirmation input */}
                {requireConfirmation && confirmationText && (
                  <Box>
                    <Text fontSize="sm" marginBottom="8px">
                      {confirmationPlaceholder || `Type "${confirmationText}" to confirm:`}
                    </Text>
                    <Input
                      value={confirmationInput}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setConfirmationInput(e.target.value)
                      }
                      placeholder={confirmationText}
                    />
                  </Box>
                )}

                {/* Not soft delete warning */}
                {!isSoftDelete && (
                  <Text fontSize="xs" color="#4B5563">
                    <Icon as={FiInfo} width="12px" height="12px" display="inline" marginRight="4px">
                      <FiInfo />
                    </Icon>
                    This action cannot be undone.
                  </Text>
                )}
              </>
            )}

            {/* Actions */}
            <Flex justifyContent="flex-end" gap={2}>
              {error && onRetry ? (
                <>
                  <Button variant="ghost" onClick={onClose} disabled={isDeleting}>
                    Close
                  </Button>
                  <Button onClick={onRetry} disabled={isDeleting}>
                    Retry
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={onClose} disabled={isDeleting}>
                    {cancelButtonText}
                  </Button>
                  <Button
                    colorPalette={deleteButtonColorScheme}
                    onClick={handleDelete}
                    disabled={!canDelete}
                    isLoading={isDeleting}
                    leftIcon={<FiTrash2 />}
                  >
                    {deleteButtonText ||
                      (isBulkDelete ? `Delete ${selectedCount} items` : 'Delete')}
                  </Button>
                </>
              )}
            </Flex>
          </Stack>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog.Root>
  );
}
